/**
 * LinenPicker — modal swatch gallery for selecting a linen cover material.
 *
 * The 91 swatches ship as bundled WebPs in `/public/linen/<CODE>.webp` and
 * are sourced from `/data/linen-catalog.json`. Swatches are grouped by their
 * code prefix (TCA, TDM, TOC, BRI, NAT) so the operator can navigate by
 * supplier collection.
 *
 * Selecting a swatch fires `onSelect({ code, color, image })`. The picker
 * is purely controlled — the parent owns the chosen value.
 */
import { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Check, Search, X } from "lucide-react";
import linenCatalog from "../data/linen-catalog.json";

const COLLECTION_LABELS = {
  TCA: "TCA Collection",
  TDM: "TDM Collection",
  TOC: "TOC Collection",
  BRI: "BRI Collection",
  NAT: "NAT Natural Series",
};

function collectionKey(code) {
  return code.replace(/[^A-Za-z]/g, "").slice(0, 3).toUpperCase();
}

export default function LinenPicker({ open, onClose, selected, onSelect }) {
  const [tab, setTab] = useState("ALL");
  const [q, setQ] = useState("");

  const grouped = useMemo(() => {
    const groups = {};
    linenCatalog.forEach((l) => {
      const k = collectionKey(l.code);
      groups[k] = groups[k] || [];
      groups[k].push(l);
    });
    return groups;
  }, []);

  const filtered = useMemo(() => {
    const all = tab === "ALL" ? linenCatalog : (grouped[tab] || []);
    if (!q.trim()) return all;
    const needle = q.trim().toUpperCase();
    return all.filter((l) =>
      l.code.toUpperCase().includes(needle) || l.color.toUpperCase().includes(needle)
    );
  }, [tab, q, grouped]);

  const tabs = ["ALL", ...Object.keys(grouped)];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="!max-w-5xl w-[95vw] max-h-[90vh] p-0 overflow-hidden flex flex-col"
        data-testid="linen-picker-dialog"
      >
        <DialogHeader className="px-6 py-4 border-b border-slate-200 bg-slate-50">
          <DialogTitle className="font-display text-xl font-black text-kolt-inkwell flex items-center justify-between">
            <span>Choose your Linen cover</span>
            <button onClick={onClose} className="text-slate-500 hover:text-slate-900" aria-label="Close">
              <X className="w-5 h-5" />
            </button>
          </DialogTitle>
          <p className="text-sm text-slate-500">
            {linenCatalog.length} European bookbinding linens across 5 collections. Click any swatch to apply.
          </p>
        </DialogHeader>

        {/* Search + tabs */}
        <div className="px-6 py-3 border-b border-slate-200 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by code or color…"
              className="w-full border border-slate-300 pl-8 pr-3 py-2 text-sm focus:border-kolt-orange focus:outline-none rounded-sm"
              data-testid="linen-search"
            />
          </div>
          <div className="flex flex-wrap gap-1">
            {tabs.map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                data-testid={`linen-tab-${t}`}
                className={`px-3 py-1.5 text-[11px] font-mono tracking-[0.18em] uppercase font-bold transition-colors rounded-sm ${
                  tab === t
                    ? "bg-kolt-orange text-white"
                    : "bg-white text-slate-600 border border-slate-300 hover:border-kolt-orange hover:text-kolt-orange"
                }`}
              >
                {t === "ALL" ? `All · ${linenCatalog.length}` : `${t} · ${(grouped[t] || []).length}`}
              </button>
            ))}
          </div>
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-y-auto p-4 bg-slate-50/50">
          {tab === "ALL" && !q ? (
            // Show grouped sections
            <div className="space-y-6">
              {Object.entries(grouped).map(([k, items]) => (
                <div key={k}>
                  <div className="overline mb-2 px-1">
                    {COLLECTION_LABELS[k] || k} · {items.length}
                  </div>
                  <Grid items={items} selected={selected} onSelect={onSelect} />
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-slate-400 font-mono text-xs uppercase tracking-widest">
              No swatches match your filter.
            </div>
          ) : (
            <Grid items={filtered} selected={selected} onSelect={onSelect} />
          )}
        </div>

        {selected && (
          <div className="px-6 py-3 border-t border-slate-200 bg-white flex items-center justify-between" data-testid="linen-current-selection">
            <div className="flex items-center gap-3">
              <img src={selected.image} alt={selected.color} className="w-10 h-10 object-cover rounded-sm border border-slate-200" />
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Currently selected</div>
                <div className="font-bold text-sm text-kolt-inkwell">
                  <span className="font-mono">{selected.code}</span> · {selected.color}
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-kolt-orange text-white font-mono text-[11px] tracking-[0.18em] uppercase font-bold hover:bg-[#9A3F12] transition-colors rounded-sm"
              data-testid="linen-confirm-btn"
            >
              Confirm selection
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Grid({ items, selected, onSelect }) {
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-7 gap-2">
      {items.map((l) => {
        const isActive = selected && selected.code === l.code;
        return (
          <button
            key={l.code}
            onClick={() => onSelect(l)}
            data-testid={`linen-swatch-${l.code}`}
            className={`group relative bg-white border-2 transition-all overflow-hidden text-left ${
              isActive ? "border-kolt-orange ring-2 ring-kolt-orange/30" : "border-transparent hover:border-kolt-orange"
            }`}
          >
            <div className="aspect-square overflow-hidden bg-slate-100">
              <img src={l.image} alt={l.color} loading="lazy"
                   className="w-full h-full object-cover group-hover:scale-[1.06] transition-transform duration-500" />
            </div>
            {isActive && (
              <div className="absolute top-1 right-1 bg-kolt-orange text-white p-1 rounded-sm">
                <Check className="w-3 h-3" />
              </div>
            )}
            <div className="p-2">
              <div className="font-mono text-[10px] font-bold text-kolt-inkwell tracking-tight truncate">{l.code}</div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider truncate">{l.color}</div>
            </div>
          </button>
        );
      })}
    </div>
  );
}
